import { LogoWithoutText } from '@c/icons/logoWithoutText';
import { ShieldCheckIcon } from '@heroicons/react/20/solid';
import type { PublicUserDocument } from '@models/user';
import { useQuery } from '@tanstack/react-query';
import { getPublicUserDoc } from '@util/firestore/users';
import { useNotifications } from '@util/hooks/useNotifcations';
import { cn } from '@util/index';
import { hasSupportPermission } from '@util/support';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import SafeImage from './SafeImage';

const styles = cva(
  'relative overflow-hidden flex items-center justify-center',
  {
    variants: {
      size: {
        mxExtraSmall: 'h-[2.8rem] w-[2.8rem]',
        extraSmall: 'h-[3.2rem] w-[3.2rem]',
        small: 'h-touch w-touch',
        medium: 'h-[5.6rem] w-[5.6rem]',
        mlarge: 'h-[7rem] w-[7rem]',
        large: 'h-[9.6rem] w-[9.6rem]',
        bottomBar: 'size-12',
      },
      circle: {
        true: 'rounded-full',
        false: 'rounded-none',
        undefined: 'rounded-none',
      },
    },
    defaultVariants: {
      size: 'medium',
    },
  }
);
interface AvatarProps extends VariantProps<typeof styles> {
  size?:
    | 'extraSmall'
    | 'mxExtraSmall'
    | 'small'
    | 'medium'
    | 'mlarge'
    | 'large'
    | 'bottomBar';
  user?: Partial<
    Pick<PublicUserDocument, 'username' | 'photo' | 'is_verified' | 'uid'>
  > | null;
  showNotificationCount?: boolean;
  showBadge?: boolean;
  circle?: boolean;
  className?: string;
}

const Avatar = ({
  user,
  size,
  showNotificationCount,
  showBadge,
  circle = true,
  className,
}: AvatarProps) => {
  let src: string | undefined,
    alt: string | undefined,
    initials: string | undefined;
  if (user) {
    src = user.photo;
    alt = user.username;
    initials = user.username?.slice(0, 2);
  }

  // Check if this is the support user - show logo instead of regular avatar
  const isSupportUser = hasSupportPermission(user?.uid);

  const { total } = useNotifications();

  // Used to get the user's photo if it's not available (seller profile page for non sellers)
  const { data: fallbackDoc } = useQuery({
    queryKey: ['publicUserUsername', user?.username],
    queryFn: () => getPublicUserDoc({ username: user?.username ?? '' }),
    enabled: !user?.photo && !!user?.username,
  });

  return (
    <div className="relative">
      <div className={cn(styles({ size, circle }), className)}>
        {isSupportUser ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black p-3">
            <LogoWithoutText className="h-full w-full" fill="white" />
          </div>
        ) : src ? (
          <SafeImage
            src={src}
            alt={alt || 'Profile Picture'}
            width={256}
            height={256}
            fill
            key={src}
            fallbackSrc={`https://ui-avatars.com/api/?background=random&length=2&name=${
              initials ?? 'NA'
            }&size=256`}
            style={{ objectFit: 'cover' }}
          />
        ) : fallbackDoc ? (
          <SafeImage
            src={fallbackDoc.photo}
            alt={alt || 'Profile Picture'}
            width={256}
            height={256}
            fill
            key={fallbackDoc.photo}
            fallbackSrc={`https://ui-avatars.com/api/?background=random&length=2&name=${
              initials ?? 'NA'
            }&size=256`}
            style={{ objectFit: 'cover' }}
          />
        ) : (
          <div
            className={cn(
              'absolute inset-0 flex items-center justify-center bg-brand-secondary',
              circle && '!rounded-full'
            )}
          >
            <span className="text-[1.5rem] font-bold uppercase text-brand-white">
              {initials || 'NA'}
            </span>
          </div>
        )}
      </div>
      {showNotificationCount && total > 0 && (
        <>
          {/* red ring after element  */}

          <div className="absolute -right-1 -top-3 flex min-h-8 min-w-8 items-center justify-center rounded-full border-[.2rem] border-brand-white bg-brand-secondary px-2 text-[1rem] font-medium text-brand-white">
            {total}
          </div>
        </>
      )}
      {user?.is_verified && showBadge && (
        <div className="absolute  -bottom-[0.8rem] -right-[0.8rem] flex h-[2rem] w-[2rem] items-center justify-center  rounded-full bg-brand-secondary p-1 ">
          <ShieldCheckIcon fill="white" width={20} height={20} />
        </div>
      )}
    </div>
  );
};

export default Avatar;
